'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import Button from '@/components/Button';

import { useLanguage } from '@/contexts/LanguageContext';
import { redirectToWhatsApp } from '@/utils/whatsapp';

// Type definitions
interface Content {
  title: string;
  intro: string;
  image?: string;
  sections?: Section[];
  faq?: FAQItem[];
  pricing?: PricingItem[];
  learnMoreButton?: string;
  appointmentButton?: string;
}

interface Section {
  title: string;
  content: string | {
    education?: string;
    educationValue?: string;
    experience?: string;
    experienceValue?: string;
    expertise?: string;
    expertiseValue?: string;
    text1?: string;
    text2?: string;
    text3?: string;
    text4?: string;
    europeanBoard?: string;
    turkishBoard?: string;
    description?: string;
    skills?: Skill[];
  };
}

interface Skill {
  name: string;
  percentage: number;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface PricingItem {
  type: string;
  price: string;
  note: string;
}

interface PageData {
  content: Content;
}

interface PageContentProps {
  pageData: {
    tr: PageData;
    en: PageData;
    de: PageData;
    currentLang: string;
  };
}

export default function PageContent({ pageData }: PageContentProps) {
  const { locale } = useLanguage();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const content = pageData[locale as keyof typeof pageData] as PageData;
  
  // Debug info box (always visible for now)
  const [slugInfo, setSlugInfo] = useState('');
  useEffect(() => {
    setSlugInfo(window.location.pathname);
  }, []);
  
  // Determine design type and page type
  let designType = 'Standart içerik tasarımı';
  let pageType = 'standart';
  if (content?.content?.title === 'Op. Dr. Barış Çin' || content?.content?.title === 'Dr. Barış Çin') {
    designType = 'Op. Dr. Barış Çin özel tasarım';
    pageType = 'doktor';
  } else if (content?.content?.title?.toLowerCase().includes('mezoterapi')) {
    pageType = 'mezoterapi';
  } else if (content?.content?.title?.toLowerCase().includes('hakkımızda') || content?.content?.title?.toLowerCase().includes('about')) {
    pageType = 'hakkımızda';
  }

  // Skin Filler özel tasarım: slug kontrolü (kullanılmadığı için _ prefix ekledik)
  const _isSkinFiller = pathname.includes('cilt-dolgusu') || 
                       pathname.includes('skin-filler') || 
                       pathname.includes('hautfuller');

  // İçerik yapısını kontrol et ve uygun şekilde render et (kullanılmadığı için _ prefix ekledik)
  const _renderContent = (section: Section) => {
    if (typeof section.content === 'string') {
      return <p className="text-gray-700">{section.content}</p>;
    }

    if (section.title === 'Eğitim ve Deneyim' || section.title === 'Education & Experience' || section.title === 'Ausbildung & Erfahrung') {
      const sectionContent = section.content as {
        education?: string;
        educationValue?: string;
        experience?: string;
        experienceValue?: string;
        expertise?: string;
        expertiseValue?: string;
      };
      
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-gray-700 text-base">
          <div>{sectionContent.education}:</div>
          <div className="font-semibold">{sectionContent.educationValue}</div>
          <div>{sectionContent.experience}:</div>
          <div className="font-semibold">{sectionContent.experienceValue}</div>
          <div>{sectionContent.expertise}:</div>
          <div className="font-semibold">{sectionContent.expertiseValue}</div>
        </div>
      );
    }

    if (section.title === 'Biyografi' || section.title === 'Biography' || section.title === 'Biografie') {
      const sectionContent = section.content as {
        text1?: string;
        text2?: string;
        europeanBoard?: string;
        turkishBoard?: string;
      };
      
      return (
        <>
          <p className="text-gray-700 mb-4">{sectionContent.text1}</p>
          <p className="text-gray-700 mb-4">{sectionContent.text2}</p>
          <div className="text-gray-700">
            <p>{sectionContent.europeanBoard}</p>
            <p>{sectionContent.turkishBoard}</p>
          </div>
        </>
      );
    }

    if (section.title === 'Profesyonel Beceriler' || section.title === 'Professional Skills' || section.title === 'Professionelle Fähigkeiten') {
      const sectionContent = section.content as {
        description?: string;
        skills?: Skill[];
      };
      
      return (
        <>
          <p className="mb-6 text-gray-700">{sectionContent.description}</p>
          <div className="space-y-4">
            {sectionContent.skills?.map((skill: Skill, index: number) => (
              <div key={index}>
                <div className="flex justify-between mb-1">
                  <span>{skill.name}</span>
                  <span>{skill.percentage}%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-blue-600 rounded-full"
                    style={{ width: `${skill.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    if (section.title === 'Kariyer Yolculuğu' || section.title === 'Career Journey' || section.title === 'Karriereweg') {
      const sectionContent = section.content as {
        text1?: string;
        text2?: string;
        text3?: string;
        text4?: string;
      };
      
      return (
        <>
          <p className="mb-4 text-gray-700">{sectionContent.text1}</p>
          <p className="mb-4 text-gray-700">{sectionContent.text2}</p>
          <p className="mb-4 text-gray-700">{sectionContent.text3}</p>
          <p className="mb-4 text-gray-700">{sectionContent.text4}</p>
        </>
      );
    }

    if (section.title === 'Akademik Eğitim' || section.title === 'Academic Education' || section.title === 'Akademische Ausbildung') {
      const sectionContent = section.content as {
        text1?: string;
        text2?: string;
      };
      
      return (
        <>
          <p className="mb-4 text-gray-700">{sectionContent.text1}</p>
          <p className="mb-4 text-gray-700">{sectionContent.text2}</p>
        </>
      );
    }

    return null;
  };

  // Sidebar'lı özel sayfa kontrolü (cilt dolgusu, mezoterapi, vs.)
  const isMezoterapi = [
    'mezoterapi',
    'mesotherapy',
    'mesotherapie'
  ].some(slug => pathname.includes(slug));

  const hasSidebar = content.content.faq || content.content.pricing;

  if (hasSidebar) {
    // Use the image from content.content.image if available, otherwise mezoterapi
    const heroImage = content?.content?.image || '/images/mezoterapi.webp';
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
        {/* Hero Section */}
        <section className="relative max-w-7xl mx-auto px-4 pt-32 pb-12 flex flex-col md:flex-row items-center justify-between overflow-hidden rounded-3xl shadow-xl bg-white">
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100 via-blue-50 to-transparent pointer-events-none z-0" />
          {/* Left: Text */}
          <div className="relative z-10 flex-1 text-center md:text-left">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm mb-6">
              <Link href="/" className="text-gray-500 hover:text-blue-600 hidden sm:inline">{locale === 'tr' ? 'Ana Sayfa' : locale === 'en' ? 'Home' : 'Startseite'}</Link>
              <svg className="w-4 h-4 text-gray-400 hidden sm:inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
              <Link href={`/${locale}/${locale === 'tr' ? 'ameliyatsiz-estetik' : locale === 'en' ? 'non-surgical-aesthetics' : 'nicht-chirurgische-aesthetik'}`} className="text-gray-500 hover:text-blue-600">{locale === 'tr' ? 'Ameliyatsız Estetik' : locale === 'en' ? 'Non-Surgical Aesthetics' : 'Nicht-chirurgische Ästhetik'}</Link>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-blue-600 font-medium">{locale === 'tr' ? content.content.title : locale === 'en' ? 'Mesotherapy' : 'Mesotherapie'}</span>
            </nav>
            <h1 className="text-5xl font-bold mb-6 text-[#181C32]">{content.content.title}</h1>
            <p className="text-lg text-gray-600 mb-8">{content.content.intro}</p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Button
                variant="primary"
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                onClick={() => redirectToWhatsApp(locale)}
              >
                {locale === 'tr' ? 'Randevu Al' : locale === 'en' ? 'Book an Appointment' : 'Termin Vereinbaren'}
              </Button>
            </div>
          </div>
          {/* Right: Illustration */}
          <div className="relative z-10 flex-1 flex justify-center md:justify-end mt-8 md:mt-0">
            <Image src={heroImage} alt={content.content.title} width={640} height={640} className="object-contain rounded-2xl shadow-lg w-full max-w-[640px]" />
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Left Content */}
            <div className="lg:col-span-2 space-y-8">
              {content.content.sections?.map((section: Section, index: number) => (
                <div key={index} className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#181C32] mb-4">{section.title}</h2>
                  <p className="text-gray-600 mb-4">{section.content as string}</p>
                </div>
              ))}
            </div>

            {/* Right Sidebar */}
            <div className="space-y-6">
              {content.content.faq && (
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="text-xl font-bold text-[#181C32] mb-4">FAQ</h3>
                  <div className="space-y-4">
                    {content.content.faq?.map((faq: FAQItem, index: number) => (
                      <div key={index}>
                        <p className="text-[#181C32] font-semibold mb-2">{faq.question}</p>
                        <p className="text-gray-600 text-sm">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {content.content.pricing && (
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="text-xl font-bold text-[#181C32] mb-4">Pricing</h3>
                  <div className="space-y-4">
                    {content.content.pricing?.map((item: PricingItem, index: number) => (
                      <div key={index}>
                        <p className="text-sm text-gray-500">{item.type}</p>
                        <p className="text-[#181C32] font-semibold">{item.price}</p>
                        <p className="text-gray-600 text-sm mt-1">{item.note}</p>
                      </div>
                    ))}
                    <Button
                      variant="primary"
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      {content.content.appointmentButton || 'Book an Appointment'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Op. Dr. Barış Çin sayfası için özel render
  if (content?.content?.title === 'Op. Dr. Barış Çin' || content?.content?.title === 'Dr. Barış Çin') {
    // Localization
    const labels = {
      tr: {
        doctorInfo: 'Doktor Bilgileri',
        name: 'Doktor İsmi',
        specialty: 'Uzmanlık Alanı',
        email: 'E-mail',
        phone: 'Telefon',
        social: 'Sosyal Medya',
        educationExp: 'Eğitim & Deneyim',
        education: 'Eğitim',
        experience: 'Deneyim',
        expertise: 'Uzmanlık',
        biography: 'Biyografi',
        skills: 'Profesyonel Yetenekler',
      },
      en: {
        doctorInfo: 'Doctor Information',
        name: 'Doctor Name',
        specialty: 'Specialty',
        email: 'E-mail',
        phone: 'Phone',
        social: 'Social Media',
        educationExp: 'Education & Experience',
        education: 'Education',
        experience: 'Experience',
        expertise: 'Expertise',
        biography: 'Biography',
        skills: 'Professional Skills',
      },
      de: {
        doctorInfo: 'Arztinformationen',
        name: 'Arztname',
        specialty: 'Fachgebiet',
        email: 'E-Mail',
        phone: 'Telefon',
        social: 'Soziale Medien',
        educationExp: 'Ausbildung & Erfahrung',
        education: 'Ausbildung',
        experience: 'Erfahrung',
        expertise: 'Fachgebiet',
        biography: 'Biografie',
        skills: 'Professionelle Fähigkeiten',
      }
    };
    const l = labels[locale as 'tr' | 'en' | 'de'];
    const info = content.content;
    const doctorEmail = 'bariscin@example.com';
    const doctorPhone = '(555) 123-4567';
    
    // Type assertions for doctor page sections
    const educationSection = info.sections?.[0]?.content as {
      educationValue?: string;
      experienceValue?: string;
      expertiseValue?: string;
    };
    const biographySection = info.sections?.[1]?.content as {
      text1?: string;
      text2?: string;
      europeanBoard?: string;
      turkishBoard?: string;
    };
    const skillsSection = info.sections?.[2]?.content as {
      description?: string;
      skills?: Skill[];
    };
    
    return (
      <>
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-900 p-4 mb-6 rounded text-sm">
          <div className="font-bold mb-1">[DEBUG] Sayfa Verisi</div>
          <div><b>Aktif dil:</b> {locale}</div>
          <div><b>Sayfa başlığı:</b> {content?.content?.title}</div>
          <div><b>İçerik kaynağı:</b> <code>getPageData fonksiyonu (page.tsx)</code></div>
          <div><b>Section sayısı:</b> {content?.content?.sections?.length}</div>
          <div><b>Section başlıkları:</b> {content?.content?.sections?.map((s: Section) => s.title).join(', ')}</div>
          <div><b>Tasarım tipi:</b> {designType}</div>
          <div><b>Sayfa tipi:</b> {pageType}</div>
          <div><b>Slug/Path:</b> {slugInfo}</div>
        </div>
        <div className="min-h-screen bg-[#f4f7fd] py-10">
          <div className="max-w-6xl mx-auto flex flex-col gap-8">
            {/* Üst: Fotoğraf + Bilgi */}
            <div className="flex flex-col md:flex-row gap-8">
              {/* Sol: Fotoğraf */}
              <div className="bg-white rounded-2xl shadow-lg p-6 flex items-center justify-center md:w-1/2 w-full">
                <Image src="/images/mezoterapi.webp" alt={info.title} width={260} height={260} className="rounded-xl object-cover w-60 h-60" />
              </div>
              {/* Sağ: Bilgi Kartı */}
              <div className="bg-white rounded-2xl shadow-lg p-8 flex-1 flex flex-col justify-center md:w-1/2 w-full">
                <h2 className="text-2xl font-bold mb-4">{l.doctorInfo}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-gray-700 text-base mb-4">
                  <div>{l.name}:</div>
                  <div className="font-semibold">{info.title}</div>
                  <div>{l.specialty}:</div>
                  <div className="font-semibold">{info.intro}</div>
                  <div>{l.email}:</div>
                  <div className="font-semibold">{doctorEmail}</div>
                  <div>{l.phone}:</div>
                  <div className="font-semibold">{doctorPhone}</div>
                  <div>{l.social}:</div>
                  <div className="flex gap-2">
                    <a href="#" aria-label="LinkedIn"><svg className="w-5 h-5 text-blue-700" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-9h3v9zm-1.5-10.28c-.97 0-1.75-.79-1.75-1.75s.78-1.75 1.75-1.75 1.75.79 1.75 1.75-.78 1.75-1.75 1.75zm13.5 10.28h-3v-4.5c0-1.08-.02-2.47-1.5-2.47-1.5 0-1.73 1.17-1.73 2.39v4.58h-3v-9h2.89v1.23h.04c.4-.75 1.38-1.54 2.84-1.54 3.04 0 3.6 2 3.6 4.59v4.72z"/></svg></a>
                    <a href="#" aria-label="Instagram"><svg className="w-5 h-5 text-pink-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.2c3.2 0 3.584.012 4.85.07 1.17.056 1.97.24 2.43.41.59.22 1.01.48 1.45.92.44.44.7.86.92 1.45.17.46.354 1.26.41 2.43.058 1.266.07 1.65.07 4.85s-.012 3.584-.07 4.85c-.056 1.17-.24 1.97-.41 2.43-.22.59-.48 1.01-.92 1.45-.44.44-.86.7-1.45.92-.46.17-1.26.354-2.43.41-1.266.058-1.65.07-4.85.07s-3.584-.012-4.85-.07c-1.17-.056-1.97-.24-2.43-.41-.59-.22-1.01-.48-1.45-.92-.44-.44-.7-.86-.92-1.45-.17-.46-.354-1.26-.41-2.43C2.212 15.784 2.2 15.4 2.2 12s.012-3.584.07-4.85c.056-1.17.24-1.97.41-2.43.22-.59.48-1.01.92-1.45.44-.44.86-.7 1.45-.92.46-.17 1.26-.354 2.43-.41C8.416 2.212 8.8 2.2 12 2.2zm0-2.2C8.736 0 8.332.012 7.052.07c-1.28.058-2.16.25-2.91.53-.8.3-1.48.7-2.15 1.37-.67.67-1.07 1.35-1.37 2.15-.28.75-.472 1.63-.53 2.91C.012 8.332 0 8.736 0 12c0 3.264.012 3.668.07 4.948.058 1.28.25 2.16.53 2.91.3.8.7 1.48 1.37 2.15.67.67 1.35 1.07 2.15 1.37.75.28 1.63.472 2.91.53C8.332 23.988 8.736 24 12 24s3.668-.012 4.948-.07c1.28-.058 2.16-.25 2.91-.53.8-.3 1.48-.7 2.15-1.37.67-.67 1.07-1.35 1.37-2.15.28-.75.472-1.63.53-2.91.058-1.28.07-1.684.07-4.948 0-3.264-.012-3.668-.07-4.948-.058-1.28-.25-2.16-.53-2.91-.3-.8-.7-1.48-1.37-2.15-.67-.67-1.35-1.07-2.15-1.37-.75-.28-1.63-.472-2.91-.53C15.668.012 15.264 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a3.999 3.999 0 1 1 0-7.998 3.999 3.999 0 0 1 0 7.998zm6.406-11.845a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z"/></svg></a>
                    <a href="#" aria-label="Twitter"><svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557a9.93 9.93 0 0 1-2.828.775 4.932 4.932 0 0 0 2.165-2.724c-.951.564-2.005.974-3.127 1.195a4.92 4.92 0 0 0-8.384 4.482C7.691 8.095 4.066 6.13 1.64 3.161c-.542.929-.856 2.01-.857 3.17 0 2.188 1.115 4.116 2.823 5.247a4.904 4.904 0 0 1-2.229-.616c-.054 2.281 1.581 4.415 3.949 4.89a4.936 4.936 0 0 1-2.224.084c.627 1.956 2.444 3.377 4.6 3.417A9.867 9.867 0 0 1 0 21.543a13.94 13.94 0 0 0 7.548 2.212c9.058 0 14.009-7.513 14.009-14.009 0-.213-.005-.425-.014-.636A10.012 10.012 0 0 0 24 4.557z"/></svg></a>
                  </div>
                </div>
                <h2 className="text-xl font-bold mt-8 mb-2">{l.educationExp}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-gray-700 text-base">
                  <div>{l.education}:</div>
                  <div className="font-semibold">{educationSection?.educationValue}</div>
                  <div>{l.experience}:</div>
                  <div className="font-semibold">{educationSection?.experienceValue}</div>
                  <div>{l.expertise}:</div>
                  <div className="font-semibold">{educationSection?.expertiseValue}</div>
                </div>
              </div>
            </div>
            {/* Alt: Biyografi ve Yetenekler */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
              {/* Biyografi */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4">{l.biography}</h3>
                <p className="mb-2 text-gray-700">{biographySection?.text1}</p>
                <p className="mb-2 text-gray-700">{biographySection?.text2}</p>
                <div className="text-gray-600 text-sm mt-2">
                  <p>{biographySection?.europeanBoard}</p>
                  <p>{biographySection?.turkishBoard}</p>
                </div>
              </div>
              {/* Profesyonel Yetenekler */}
              <div className="bg-blue-700 rounded-2xl shadow-lg p-6 text-white">
                <h3 className="text-xl font-bold mb-4">{l.skills}</h3>
                <p className="mb-4 text-blue-100">{skillsSection?.description}</p>
                <div className="space-y-4">
                  {skillsSection?.skills?.map((skill: Skill, idx: number) => (
                    <div key={idx}>
                      <div className="flex justify-between mb-1">
                        <span>{skill.name}</span>
                        <span>{skill.percentage}%</span>
                      </div>
                      <div className="w-full h-2 bg-blue-200 rounded-full">
                        <div
                          className="h-2 bg-white rounded-full"
                          style={{ width: `${skill.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Diğer sayfalar için standart render
  return (
    <>
      <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-900 p-4 mb-6 rounded text-sm">
        <div className="font-bold mb-1">[DEBUG] Sayfa Verisi</div>
        <div><b>Aktif dil:</b> {locale}</div>
        <div><b>Sayfa başlığı:</b> {content?.content?.title}</div>
        <div><b>İçerik kaynağı:</b> <code>getPageData fonksiyonu (page.tsx)</code></div>
        <div><b>Section sayısı:</b> {content?.content?.sections?.length}</div>
        <div><b>Section başlıkları:</b> {content?.content?.sections?.map((s: Section) => s.title).join(', ')}</div>
        <div><b>Tasarım tipi:</b> {designType}</div>
        <div><b>Sayfa tipi:</b> {pageType}</div>
        <div><b>Slug/Path:</b> {slugInfo}</div>
      </div>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
        {/* Hero Section */}
        <section className="relative max-w-7xl mx-auto px-4 pt-32 pb-12 flex flex-col md:flex-row items-center justify-between overflow-hidden rounded-3xl shadow-xl bg-white">
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100 via-blue-50 to-transparent pointer-events-none z-0" />
          {/* Left: Text */}
          <div className="relative z-10 flex-1 text-center md:text-left">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm mb-6">
              <Link href="/" className="text-gray-500 hover:text-blue-600 hidden sm:inline">
                {locale === 'tr' ? 'Ana Sayfa' : locale === 'en' ? 'Home' : 'Startseite'}
              </Link>
              <svg className="w-4 h-4 text-gray-400 hidden sm:inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
              <Link href={`/${locale}/${locale === 'tr' ? 'ameliyatsiz-estetik' : locale === 'en' ? 'non-surgical-aesthetics' : 'nicht-chirurgische-aesthetik'}`} className="text-gray-500 hover:text-blue-600">{locale === 'tr' ? 'Ameliyatsız Estetik' : locale === 'en' ? 'Non-Surgical Aesthetics' : 'Nicht-chirurgische Ästhetik'}</Link>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-blue-600 font-medium">{content.content.title}</span>
            </nav>
            <h1 className="text-5xl font-bold mb-6 text-[#181C32]">{content.content.title}</h1>
            <p className="text-lg text-gray-600 mb-8">{content.content.intro}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="primary"
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                onClick={() => redirectToWhatsApp(locale)}
              >
                {locale === 'tr' ? 'Randevu Al' : locale === 'en' ? 'Book an Appointment' : 'Termin Vereinbaren'}
              </Button>
            </div>
          </div>
          {/* Right: Image */}
          <div className="relative z-10 flex-1 flex justify-center md:justify-end mt-8 md:mt-0">
            <Image
              src={content?.content?.image || '/images/mezoterapi.webp'}
              alt={content.content.title}
              width={640} 
              height={640} 
              className="object-contain rounded-2xl shadow-lg w-full max-w-[640px]" 
              priority={true}
            />
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Left Content */}
            <div className="lg:col-span-2 space-y-8">
              {content?.content?.sections?.map((section: Section, index: number) => (
                <div key={index} className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#181C32] mb-4">{section.title}</h2>
                  <p className="text-gray-600 dark:text-gray-300">{section.content as string}</p>
                </div>
              ))}
            </div>
            {/* Right Sidebar */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-[#181C32] mb-4">
                  {locale === 'tr' ? 'Hızlı Bilgiler' : locale === 'en' ? 'Quick Info' : 'Schnellinfo'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">
                      {locale === 'tr' ? 'Uzmanlık' : locale === 'en' ? 'Specialization' : 'Spezialisierung'}
                    </p>
                    <p className="text-[#181C32] font-semibold">
                      {locale === 'tr' ? 'Plastik Cerrahi' : locale === 'en' ? 'Plastic Surgery' : 'Plastische Chirurgie'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {locale === 'tr' ? 'Deneyim' : locale === 'en' ? 'Experience' : 'Erfahrung'}
                    </p>
                    <p className="text-[#181C32] font-semibold">15+ yıl</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {locale === 'tr' ? 'Eğitim' : locale === 'en' ? 'Education' : 'Ausbildung'}
                    </p>
                    <p className="text-[#181C32] font-semibold">
                      {locale === 'tr' ? 'Marmara Üniversitesi' : locale === 'en' ? 'Marmara University' : 'Marmara Universität'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-[#181C32] mb-4">
                  {locale === 'tr' ? 'İletişim' : locale === 'en' ? 'Contact' : 'Kontakt'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">
                      {locale === 'tr' ? 'Adres' : locale === 'en' ? 'Address' : 'Adresse'}
                    </p>
                    <p className="text-[#181C32] font-semibold">
                      {locale === 'tr' ? 'Kadıköy, İstanbul' : locale === 'en' ? 'Kadıköy, Istanbul' : 'Kadıköy, Istanbul'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {locale === 'tr' ? 'Telefon' : locale === 'en' ? 'Phone' : 'Telefon'}
                    </p>
                    <p className="text-[#181C32] font-semibold">+90 555 123 4567</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {locale === 'tr' ? 'E-posta' : locale === 'en' ? 'Email' : 'E-Mail'}
                    </p>
                    <p className="text-[#181C32] font-semibold">info@bariscin.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
} 